import React, { FC, useCallback, useEffect, useState } from 'react';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
} from '@material-ui/core';
import useStock from '../../hooks/api/useStock';
import { Skeleton } from '@material-ui/lab';
import Supplier from '../../interfaces/api/Supplier';

interface Props {
  value?: string;
  error?: boolean;
  required?: boolean;
  helperText?: string;
  className?: any;
  domainId?: string;
  onChange: (event: React.ChangeEvent<{ name?: string; value: unknown }>) => void;
}

const SupplierSelect: FC<Props> = React.forwardRef<any, Props>(
  ({ onChange, className, helperText, required, value, domainId, ...props }, ref) => {
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();

    const { getSuppliers } = useStock();

    useEffect(() => {
      getSuppliers()
        .then((collection) => {
          setSuppliers(collection.items);
          setLoading(false);
        })
        .catch((e) => {
          setError(e.message);
          setLoading(false);
        });
    }, []);

    const sortSupplier = (supplierA: Supplier, supplierB: Supplier) => {
      return supplierA.name.localeCompare(supplierB.name);
    };

    const getSupplierByDomain = useCallback(
      () => suppliers.sort(sortSupplier).filter((supplier) => supplier.domainId === domainId),
      [suppliers, domainId]
    );

    if (loading) {
      return <Skeleton height={50} />;
    }

    if (error) {
      return <div style={{ color: '#f44336' }}>{error}</div>;
    }

    return (
      <FormControl
        ref={ref}
        className={className}
        variant='outlined'
        error={props.error}
        disabled={!domainId}
        required={required}
        {...props}
        fullWidth
      >
        <InputLabel>Fournisseur</InputLabel>
        <Select
          value={value}
          onChange={onChange}
          renderValue={(value) => `${getSupplierByDomain().find((sup) => sup.id === value)?.name || ''}`}
          input={<OutlinedInput label='Fournisseur *' />}
        >
          <MenuItem value=''>
            <ListItemText primary='' />
          </MenuItem>

          {getSupplierByDomain().map((supplier, key) => (
            <MenuItem key={key} value={supplier.id}>
              <ListItemText
                primary={supplier.name}
                secondary={
                  ['hardware', 'virtual'].includes(supplier.type) && supplier.type === 'hardware'
                    ? 'Référence produit physique'
                    : ' Référence produit virtuel'
                }
              />
            </MenuItem>
          ))}
        </Select>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }
);

export default React.memo(SupplierSelect);
